// Geodesy helpers and the lon/lat-aligned solver grid.

export type LngLat = [number, number];

export const EARTH_RADIUS = 6371008.8;
const M_PER_DEG_LAT = (Math.PI * EARTH_RADIUS) / 180;

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function distance(a: LngLat, b: LngLat): number {
  const dLat = rad(b[1] - a[1]);
  const dLon = rad(b[0] - a[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a[1])) * Math.cos(rad(b[1])) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing from a to b, degrees clockwise from north, 0..360. */
export function bearing(a: LngLat, b: LngLat): number {
  const p1 = rad(a[1]);
  const p2 = rad(b[1]);
  const dl = rad(b[0] - a[0]);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

/** Linear interpolation between two nearby points (fine for legs of a few km). */
export function lerp(a: LngLat, b: LngLat, t: number): LngLat {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

/** Point `dist` metres from `p` on `brg` degrees (flat-earth, fine for < 100 km). */
export function offset(p: LngLat, dist: number, brg: number): LngLat {
  const dn = dist * Math.cos(rad(brg));
  const de = dist * Math.sin(rad(brg));
  return [p[0] + de / (M_PER_DEG_LAT * Math.cos(rad(p[1]))), p[1] + dn / M_PER_DEG_LAT];
}

/**
 * A regular grid aligned with lon/lat. Cells are square in metres at the centre latitude, which is
 * accurate to well under 1% over the 40-60 km windows the solver uses. Row 0 is the north edge.
 */
export interface GridSpec {
  /** Grid centre (cell `cx, cy`). */
  lng0: number;
  lat0: number;
  cx: number;
  cy: number;
  nx: number;
  ny: number;
  /** Cell size in metres. */
  cell: number;
  dLng: number;
  dLat: number;
}

/** Square grid of `radius` metres around `centre`, with `centre` at the middle cell. */
export function gridAround(centre: LngLat, radius: number, cell: number): GridSpec {
  const half = Math.ceil(radius / cell);
  return makeSpec(centre, 2 * half + 1, 2 * half + 1, cell);
}

/** Grid covering a lon/lat box. */
export function gridForBox(sw: LngLat, ne: LngLat, cell: number): GridSpec {
  const centre: LngLat = [(sw[0] + ne[0]) / 2, (sw[1] + ne[1]) / 2];
  const w = distance([sw[0], centre[1]], [ne[0], centre[1]]);
  const h = (ne[1] - sw[1]) * M_PER_DEG_LAT;
  const nx = 2 * Math.ceil(w / cell / 2) + 1;
  const ny = 2 * Math.ceil(h / cell / 2) + 1;
  return makeSpec(centre, nx, ny, cell);
}

function makeSpec(centre: LngLat, nx: number, ny: number, cell: number): GridSpec {
  const dLat = cell / M_PER_DEG_LAT;
  const dLng = cell / (M_PER_DEG_LAT * Math.cos(rad(centre[1])));
  return { lng0: centre[0], lat0: centre[1], cx: (nx - 1) / 2, cy: (ny - 1) / 2, nx, ny, cell, dLng, dLat };
}

export function cellCenter(s: GridSpec, col: number, row: number): LngLat {
  return [s.lng0 + (col - s.cx) * s.dLng, s.lat0 - (row - s.cy) * s.dLat];
}

export function idxCenter(s: GridSpec, idx: number): LngLat {
  return cellCenter(s, idx % s.nx, Math.floor(idx / s.nx));
}

/** Cell containing a point, or null when outside. */
export function cellOf(s: GridSpec, p: LngLat): { col: number; row: number; idx: number } | null {
  const col = Math.round((p[0] - s.lng0) / s.dLng + s.cx);
  const row = Math.round((s.lat0 - p[1]) / s.dLat + s.cy);
  if (col < 0 || row < 0 || col >= s.nx || row >= s.ny) return null;
  return { col, row, idx: row * s.nx + col };
}

/** Outer corners for a MapLibre image source: [NW, NE, SE, SW]. */
export function gridCorners(s: GridSpec): [LngLat, LngLat, LngLat, LngLat] {
  const w = s.lng0 - (s.cx + 0.5) * s.dLng;
  const e = s.lng0 + (s.nx - s.cx - 0.5) * s.dLng;
  const n = s.lat0 + (s.cy + 0.5) * s.dLat;
  const so = s.lat0 - (s.ny - s.cy - 0.5) * s.dLat;
  return [
    [w, n],
    [e, n],
    [e, so],
    [w, so],
  ];
}

/** Total length of a polyline, metres. */
export function pathLength(pts: LngLat[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += distance(pts[i - 1], pts[i]);
  return d;
}

/** Slippy-map tile coordinates (fractional) for a point. */
export function lngLatToTile(p: LngLat, z: number): { x: number; y: number } {
  const n = 2 ** z;
  const x = ((p[0] + 180) / 360) * n;
  const latRad = rad(p[1]);
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;
  return { x, y };
}

/** Ground resolution of a 256 px web-mercator tile pixel at a latitude, metres. */
export function pixelSize(lat: number, z: number): number {
  return (2 * Math.PI * 6378137 * Math.cos(rad(lat))) / (256 * 2 ** z);
}
